import { Country, countries } from './countries'

export interface PhoneNumberParts {
  dialCode: string
  nationalNumber: string
  rawInput: string
}

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Remove all non-digit characters except the leading +
 */
export const cleanPhoneNumber = (input: string): string => {
  // Keep the + at the beginning if present
  const hasPlus = input.trim().startsWith('+')
  const cleaned = input.replace(/\D/g, '')
  return hasPlus ? `+${cleaned}` : cleaned
}

/**
 * Parse a phone number and extract dial code and national number
 */
export const parsePhoneNumber = (
  input: string,
  currentCountry?: Country
): PhoneNumberParts => {
  const cleaned = cleanPhoneNumber(input)

  // If input is empty
  if (!cleaned) {
    return {
      dialCode: currentCountry?.dialCode || '',
      nationalNumber: '',
      rawInput: input,
    }
  }

  // If input starts with + or 00, try to detect country from dial code
  if (cleaned.startsWith('+') || cleaned.startsWith('00')) {
    const numberWithoutPrefix = cleaned.startsWith('+')
      ? cleaned.substring(1)
      : cleaned.substring(2)

    // Try to find matching dial code (try longest matches first)
    // Sort countries by dial code length (descending) to match longest codes first
    const sortedCountries = [...countries].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    )

    for (const country of sortedCountries) {
      const dialCodeDigits = country.dialCode.replace(/\D/g, '')
      if (numberWithoutPrefix.startsWith(dialCodeDigits)) {
        return {
          dialCode: country.dialCode,
          nationalNumber: numberWithoutPrefix.substring(dialCodeDigits.length),
          rawInput: input,
        }
      }
    }

    // No match found, return first digits as potential dial code
    const potentialDialCode = numberWithoutPrefix.substring(0, 4)
    return {
      dialCode: `+${potentialDialCode}`,
      nationalNumber: numberWithoutPrefix.substring(potentialDialCode.length),
      rawInput: input,
    }
  }

  // If input doesn't start with + or 00, assume it's a national number
  return {
    dialCode: currentCountry?.dialCode || '',
    nationalNumber: cleaned,
    rawInput: input,
  }
}

/**
 * Detect country from phone number
 */
export const detectCountryFromNumber = (
  input: string,
  currentCountry?: Country
): Country | null => {
  const parsed = parsePhoneNumber(input, currentCountry)

  if (!parsed.dialCode) {
    return null
  }

  // Find country by dial code
  const matchingCountries = countries.filter(
    (c) => c.dialCode === parsed.dialCode
  )

  if (matchingCountries.length === 0) {
    return null
  }

  if (matchingCountries.length === 1) {
    return matchingCountries[0]
  }

  // Multiple countries with same dial code
  // Prefer current country if it matches
  if (
    currentCountry &&
    matchingCountries.some((c) => c.code === currentCountry.code)
  ) {
    return currentCountry
  }

  // Otherwise return the first match (usually the most common one)
  return matchingCountries[0]
}

/**
 * Format phone number based on country-specific patterns
 */
export const formatPhoneNumber = (input: string, country: Country): string => {
  const cleaned = cleanPhoneNumber(input)

  if (!cleaned) {
    return ''
  }

  const parsed = parsePhoneNumber(cleaned, country)
  const { nationalNumber } = parsed

  if (!nationalNumber) {
    // Only dial code entered
    return country.dialCode
  }

  // Apply country-specific formatting patterns
  const formatted = formatNationalNumber(nationalNumber, country.code)

  return `${country.dialCode} ${formatted}`
}

/**
 * Format national number with spacing based on country
 * This is a simplified version - real implementation would need more patterns
 */
const formatNationalNumber = (number: string, countryCode: string): string => {
  // Remove any non-digits
  const digits = number.replace(/\D/g, '')

  if (!digits) return ''

  // Country-specific formatting patterns
  switch (countryCode) {
    case 'US':
    case 'CA':
      // Format: (XXX) XXX-XXXX
      if (digits.length <= 3) return digits
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`

    case 'FR':
      // Format: X XX XX XX XX (French mobile/landline)
      if (digits.length <= 1) return digits
      if (digits.length <= 3) return `${digits.slice(0, 1)} ${digits.slice(1)}`
      if (digits.length <= 5)
        return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`
      if (digits.length <= 7)
        return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`
      return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`

    case 'DE':
      // Format varies, but commonly: XXX XXXXXXX or XXXX XXXXXX
      if (digits.length <= 3) return digits
      if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`
      return `${digits.slice(0, 4)} ${digits.slice(4)}`

    case 'GB':
      // Format: XXXX XXXXXX or XXXXX XXXXXX
      if (digits.length <= 4) return digits
      if (digits.length <= 10) return `${digits.slice(0, 4)} ${digits.slice(4)}`
      return `${digits.slice(0, 5)} ${digits.slice(5)}`

    case 'CH':
      // Format: XX XXX XX XX
      if (digits.length <= 2) return digits
      if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`
      if (digits.length <= 7)
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`

    case 'IT':
    case 'ES':
      // Format: XXX XXX XXX
      if (digits.length <= 3) return digits
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`

    case 'AU':
      // Format: XXXX XXX XXX
      if (digits.length <= 4) return digits
      if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`

    case 'JP':
      // Format: XX-XXXX-XXXX
      if (digits.length <= 2) return digits
      if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`
      return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6, 10)}`

    case 'CN':
      // Format: XXX XXXX XXXX
      if (digits.length <= 3) return digits
      if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`
      return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7, 11)}`

    default:
      // Generic formatting: add space every 3-4 digits
      if (digits.length <= 3) return digits
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
      if (digits.length <= 9)
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`
  }
}

/**
 * Validate phone number based on country
 */
export const validatePhoneNumber = (
  input: string,
  country: Country
): ValidationResult => {
  const cleaned = cleanPhoneNumber(input)

  if (!cleaned) {
    return { isValid: true } // Empty is valid (not required by default)
  }

  const parsed = parsePhoneNumber(cleaned, country)
  const { nationalNumber } = parsed

  // Check if we have a national number
  if (!nationalNumber || nationalNumber.length === 0) {
    return {
      isValid: false,
      error: 'Please enter a phone number',
    }
  }

  // Country-specific validation rules
  const validationRules = getValidationRules(country.code)

  if (nationalNumber.length < validationRules.minLength) {
    return {
      isValid: false,
      error: `Phone number too short (minimum ${validationRules.minLength} digits)`,
    }
  }

  if (nationalNumber.length > validationRules.maxLength) {
    return {
      isValid: false,
      error: `Phone number too long (maximum ${validationRules.maxLength} digits)`,
    }
  }

  // Validate pattern if specified
  if (
    validationRules.pattern &&
    !validationRules.pattern.test(nationalNumber)
  ) {
    return {
      isValid: false,
      error: 'Invalid phone number format',
    }
  }

  return { isValid: true }
}

/**
 * Get validation rules for a country
 */
interface ValidationRules {
  minLength: number
  maxLength: number
  pattern?: RegExp
}

const getValidationRules = (countryCode: string): ValidationRules => {
  switch (countryCode) {
    case 'US':
    case 'CA':
      return {
        minLength: 10,
        maxLength: 10,
        pattern: /^[2-9]\d{9}$/, // US/Canada: area code can't start with 0 or 1
      }

    case 'FR':
      return {
        minLength: 9,
        maxLength: 9,
        pattern: /^[1-9]\d{8}$/, // France: 9 digits, can't start with 0
      }

    case 'DE':
      return {
        minLength: 6,
        maxLength: 13,
      }

    case 'GB':
      return {
        minLength: 10,
        maxLength: 10,
      }

    case 'CH':
      return {
        minLength: 9,
        maxLength: 9,
      }

    case 'IT':
    case 'ES':
      return {
        minLength: 9,
        maxLength: 9,
      }

    case 'AU':
      return {
        minLength: 9,
        maxLength: 9,
      }

    case 'JP':
      return {
        minLength: 10,
        maxLength: 10,
      }

    case 'CN':
      return {
        minLength: 11,
        maxLength: 11,
      }

    default:
      // Generic validation
      return {
        minLength: 4,
        maxLength: 15,
      }
  }
}

/**
 * Check if input starts with international prefix (+ or 00)
 */
export const hasInternationalPrefix = (input: string): boolean => {
  const trimmed = input.trim()
  return trimmed.startsWith('+') || trimmed.startsWith('00')
}

/**
 * Get display value for input field
 * Returns the properly formatted phone number or the raw input if formatting fails
 */
export const getDisplayValue = (
  value: string,
  country: Country,
  cursorPosition: number | null
): { displayValue: string; newCursorPosition: number | null } => {
  if (!value) {
    return { displayValue: '', newCursorPosition: cursorPosition }
  }

  // If user is actively typing international format, don't auto-format yet
  if (hasInternationalPrefix(value)) {
    const parsed = parsePhoneNumber(value, country)
    if (parsed.nationalNumber) {
      const formatted = formatPhoneNumber(value, country)
      // Calculate new cursor position after formatting
      const diff = formatted.length - value.length
      const newCursorPosition =
        cursorPosition !== null ? cursorPosition + diff : null
      return { displayValue: formatted, newCursorPosition }
    }
  }

  // For national numbers, format with country dial code
  const formatted = formatPhoneNumber(value, country)
  return { displayValue: formatted, newCursorPosition: cursorPosition }
}
